import { useEffect, useMemo, useState } from 'react';
import { selectionText } from '../../../shared/annotations';
import type {
  AnnotationAction,
  PublicAnnotation,
  PublicReviewAsset,
  PublicReviewer,
  TranscriptSegment,
} from '../../../shared/types';
import Chip from '../Chip';
import Eyebrow from '../Eyebrow';
import {
  actionLabels,
  actionTone,
  DecisionIcon,
  formatReviewTime,
  reviewerAuthorLabel,
  reviewQuietButton,
} from './ReviewUi';

interface SelectsViewProps {
  assets: PublicReviewAsset[];
  segments: TranscriptSegment[];
  annotations: PublicAnnotation[];
  reviewers: PublicReviewer[];
  reviewer: PublicReviewer | null;
  /** True when this session entered through the edit-access link and may adjust everyone's selects. */
  canEditAll: boolean;
  onOpen: (assetId: string, time: number, segmentId: string) => void;
  onEdit: (annotation: PublicAnnotation, action: AnnotationAction, note: string | null) => Promise<void>;
}

export interface ReviewerFilterOption {
  id: string;
  label: string;
  isOwner: boolean;
}

/** Chips only offer reviewers who have a select in view: you first, the owner last. */
export function reviewerFilterOptions(
  selects: Array<Pick<PublicAnnotation, 'reviewer_id'>>,
  reviewers: PublicReviewer[],
  currentReviewerId: string | null,
): ReviewerFilterOption[] {
  const present = new Set(selects.map((annotation) => annotation.reviewer_id));
  const rank = (item: PublicReviewer) => (item.id === currentReviewerId ? 0 : item.is_owner ? 2 : 1);
  return reviewers
    .filter((item) => present.has(item.id))
    .sort((a, b) => rank(a) - rank(b))
    .map((item) => ({
      id: item.id,
      label: item.id === currentReviewerId ? `${reviewerAuthorLabel(item)} (you)` : reviewerAuthorLabel(item),
      isOwner: Boolean(item.is_owner),
    }));
}

export function selectsByReviewer<T extends { reviewer_id: string }>(selects: T[], reviewerId: string | null): T[] {
  return reviewerId === null ? selects : selects.filter((annotation) => annotation.reviewer_id === reviewerId);
}

export interface SelectOwnership {
  /** Whose select this is, in plain words. */
  owner: string;
  /** What you can do about it: an offer when you have edit access, a limit when you do not. */
  permission: string | null;
  /** True when the permission clause is an action, so it can carry the accent. */
  actionable: boolean;
  /** Who last adjusted someone else's select, so no change looks silent. */
  adjusted: string | null;
}

/** Says whose select it is and what you can do about it, so missing buttons never read as a bug. */
export function selectOwnership(
  author: PublicReviewer | undefined,
  editor: PublicReviewer | undefined,
  own: boolean,
  canEditAll: boolean,
): SelectOwnership {
  const adjusted = editor ? `Adjusted by ${reviewerAuthorLabel(editor)}` : null;
  if (own) return { owner: 'Your select', permission: null, actionable: false, adjusted };
  const name = reviewerAuthorLabel(author);
  const possessive = name.endsWith('s') ? `${name}’` : `${name}’s`;
  return {
    owner: `${possessive} select`,
    permission: canEditAll ? 'Adjust — you have edit access' : `only ${name} can change it`,
    actionable: canEditAll,
    adjusted,
  };
}

function DecisionControls({ annotation, own, onEdit }: Pick<SelectsViewProps, 'onEdit'> & { annotation: PublicAnnotation; own: boolean }) {
  const [note, setNote] = useState(annotation.note ?? '');
  const [commentMode, setCommentMode] = useState(false);
  const [busyAction, setBusyAction] = useState<AnnotationAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setNote(annotation.note ?? ''), [annotation.note]);

  async function change(action: AnnotationAction) {
    const nextNote = note.trim() || null;
    if (action === 'comment' && !nextNote) {
      setCommentMode(true);
      setError(null);
      return;
    }
    setBusyAction(action);
    setError(null);
    try {
      await onEdit(annotation, action, nextNote);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not save that change. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="mt-4 border-t border-line pt-4 sm:ml-[7.75rem]">
      <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-faint">{own ? 'Change decision' : 'Adjust this select'}</p>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(['use', 'maybe', 'remove', 'comment'] as const).map((action) => (
          <button
            key={action}
            type="button"
            className={`app-button inline-flex min-h-11 items-center justify-center gap-1 rounded-full border px-2 text-xs font-semibold ${actionTone[action]} ${busyAction === action ? 'ring-2 ring-current ring-offset-2' : ''}`}
            onClick={() => void change(action)}
            disabled={busyAction !== null}
            aria-label={`Change decision to ${actionLabels[action]}`}
            aria-pressed={annotation.action === action}
            aria-busy={busyAction === action || undefined}
          >
            {busyAction === action ? <span className="inline-spinner" aria-hidden="true" /> : <DecisionIcon action={action} className="h-3.5 w-3.5" />}
            {busyAction === action ? 'Saving' : actionLabels[action]}
          </button>
        ))}
      </div>
      {commentMode ? (
        <div className="mt-3 rounded-input bg-paper-200 p-3">
          <label className="block font-mono text-[9px] uppercase tracking-[0.12em] text-faint">
            Comment
            <textarea
              className="mt-1.5 min-h-16 w-full resize-y rounded-input border border-line bg-card p-2 font-sans text-sm font-normal normal-case tracking-normal text-fg"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What should we know?"
            />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={reviewQuietButton} onClick={() => void change('comment')} disabled={busyAction !== null || !note.trim()}>Save comment</button>
            <button type="button" className={reviewQuietButton} onClick={() => { setCommentMode(false); setError(null); }} disabled={busyAction !== null}>Cancel</button>
          </div>
        </div>
      ) : null}
      {error ? <p className="mt-3 text-xs text-error" role="alert">{error}</p> : null}
    </div>
  );
}

export default function SelectsView({ assets, segments, annotations, reviewers, reviewer, canEditAll, onOpen, onEdit }: SelectsViewProps) {
  const [includeMaybe, setIncludeMaybe] = useState(false);
  const [reviewerFilter, setReviewerFilter] = useState<string | null>(null);
  const reviewerById = useMemo(() => new Map(reviewers.map((item) => [item.id, item])), [reviewers]);
  const segmentIds = useMemo(() => new Set(segments.map((segment) => segment.id)), [segments]);
  const segmentsByVersion = useMemo(() => {
    const grouped = new Map<string, Array<Pick<TranscriptSegment, 'id' | 'order_index' | 'text'>>>();
    for (const segment of segments) {
      const versionSegments = grouped.get(segment.transcript_version_id) ?? [];
      versionSegments.push({ id: segment.id, order_index: segment.order_index, text: segment.text });
      grouped.set(segment.transcript_version_id, versionSegments);
    }
    return grouped;
  }, [segments]);
  const selected = annotations.filter((annotation) =>
    (annotation.action === 'use' || (includeMaybe && annotation.action === 'maybe'))
    && segmentIds.has(annotation.start_segment_id)
    && segmentIds.has(annotation.end_segment_id));
  const filterOptions = reviewerFilterOptions(selected, reviewers, reviewer?.id ?? null);
  /* Hiding Maybe can retire the reviewer you were filtering by; fall back to everyone. */
  const activeFilter = filterOptions.some((option) => option.id === reviewerFilter) ? reviewerFilter : null;
  const visible = selectsByReviewer(selected, activeFilter);

  return (
    <section aria-labelledby="selects-title" className="mx-auto max-w-6xl px-5 py-12 md:px-10 md:py-16">
      <div>
        <Eyebrow accent className="rise">Coverage check</Eyebrow>
        <h1 id="selects-title" className="title-xl rise mt-4" style={{ animationDelay: '80ms' }}>Project selects</h1>
        <p className="rise mt-4 max-w-2xl text-muted" style={{ animationDelay: '160ms' }}>Every line marked Use, gathered across the interviews. Show Maybe selects to compare the wider field.</p>
      </div>
      <div className="rise-subtle mt-7 flex flex-wrap items-center gap-2" style={{ animationDelay: '220ms' }}>
        {filterOptions.length > 1 ? (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter selects by reviewer">
            {[{ id: null, label: 'Everyone', isOwner: false }, ...filterOptions].map((option) => {
              const active = option.id === activeFilter;
              const tone = option.isOwner ? 'owner-badge' : active ? 'chip-teal' : '';
              return (
                <button
                  key={option.id ?? 'everyone'}
                  type="button"
                  className={`chip chip-button ${tone} ${active ? 'chip-button-active' : ''}`.replace(/\s+/g, ' ').trim()}
                  aria-pressed={active}
                  onClick={() => setReviewerFilter(option.id)}
                >
                  {active ? <span className="chip-dot" aria-hidden="true" /> : null}
                  {option.label}
                </button>
              );
            })}
          </div>
        ) : null}
        <label className="ml-auto flex min-h-11 items-center gap-3 rounded-full border border-line bg-card px-4 text-sm font-semibold text-muted">
          <input type="checkbox" className="h-5 w-5 accent-accent" checked={includeMaybe} onChange={(event) => setIncludeMaybe(event.target.checked)} />
          Show Maybe selects
        </label>
      </div>
      {visible.length === 0 ? (
        <div className="card-dashed rise-subtle mt-10 p-10">
          <h2 className="text-3xl">No selects yet</h2>
          <p className="mt-3 text-muted">Use picks from every interview will gather here.</p>
        </div>
      ) : (
        <div className="mt-10 space-y-12">
          {assets.map((asset, assetIndex) => {
            const assetAnnotations = visible.filter((annotation) => annotation.asset_id === asset.id);
            if (assetAnnotations.length === 0) return null;
            return (
              <section key={asset.id} aria-labelledby={`selects-${asset.id}`}>
                <Eyebrow>Interview {assetIndex + 1} · {assetAnnotations.length} {assetAnnotations.length === 1 ? 'select' : 'selects'}</Eyebrow>
                <h2 id={`selects-${asset.id}`} className="title-lg mt-3">{asset.title}</h2>
                {asset.speaker_label ? <p className="mt-1 text-sm text-muted">{asset.speaker_label}</p> : null}
                <ol className="mt-5 divide-y divide-line border-y border-line">
                  {assetAnnotations.map((annotation) => {
                    const author = reviewerById.get(annotation.reviewer_id);
                    const startSegment = segments.find((item) => item.id === annotation.start_segment_id);
                    const textSegments = startSegment ? segmentsByVersion.get(startSegment.transcript_version_id) ?? [] : [];
                    const quote = selectionText(textSegments, annotation);
                    const own = reviewer?.id === annotation.reviewer_id;
                    const editor = annotation.edited_by_reviewer_id
                      ? reviewerById.get(annotation.edited_by_reviewer_id)
                      : undefined;
                    const ownership = selectOwnership(author, editor, own, canEditAll);
                    return (
                      <li key={annotation.id} className="rise-subtle py-5">
                        <div className="grid gap-3 rounded-input p-2 sm:grid-cols-[7rem_minmax(0,1fr)_auto]">
                          <button
                            type="button"
                            className="min-h-11 rounded-input px-2 text-left font-mono text-xs text-faint hover:bg-paper-200 hover:text-accent"
                            onClick={() => onOpen(asset.id, annotation.start_s, annotation.start_segment_id)}
                            aria-label={`Open selection in transcript at ${formatReviewTime(annotation.start_s)}`}
                          >
                            {formatReviewTime(annotation.start_s)} to {formatReviewTime(annotation.end_s)}
                          </button>
                          <button
                            type="button"
                            className="min-h-11 rounded-input text-left hover:bg-paper-200"
                            onClick={() => onOpen(asset.id, annotation.start_s, annotation.start_segment_id)}
                          >
                            <span className="block font-display text-xl leading-snug">“{quote || 'Selected transcript text'}”</span>
                            {annotation.note ? <span className="mt-1 block text-sm text-muted">{annotation.note}</span> : null}
                            <span className="mt-2 block text-xs text-faint">
                              {ownership.owner}
                              {ownership.permission ? (
                                <> · <span className={ownership.actionable ? 'font-semibold text-accent' : undefined}>{ownership.permission}</span></>
                              ) : null}
                              {ownership.adjusted ? <> · {ownership.adjusted}</> : null}
                            </span>
                          </button>
                          <Chip tone={annotation.action === 'use' ? 'success' : annotation.action === 'maybe' ? 'warning' : annotation.action === 'remove' ? 'error' : 'teal'} className="self-start">
                            <DecisionIcon action={annotation.action} className="h-3.5 w-3.5" />
                            {actionLabels[annotation.action]}
                          </Chip>
                        </div>
                        {own || canEditAll ? <DecisionControls annotation={annotation} own={own} onEdit={onEdit} /> : null}
                      </li>
                    );
                  })}
                </ol>
              </section>
            );
          })}
        </div>
      )}
      <div className="mt-10"><button type="button" className={reviewQuietButton} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Back to top</button></div>
    </section>
  );
}
